const FENCE_OPEN = /^(```+|~~~+)/;
const DELIMITER_CELL = /^:?-{1,}:?$/;

function splitQuotePrefix(line: string): { prefix: string; body: string } {
  const match = line.match(/^((?:\s*>\s*)*)(.*)$/);

  return { prefix: match?.[1] ?? '', body: match?.[2] ?? line };
}

function splitUnescapedPipes(row: string): string[] {
  const cells: string[] = [];
  let current = '';

  for (let index = 0; index < row.length; index += 1) {
    const character = row[index];

    if (character !== '|') {
      current += character;
      continue;
    }

    let backslashes = 0;

    for (
      let cursor = index - 1;
      cursor >= 0 && row[cursor] === '\\';
      cursor -= 1
    ) {
      backslashes += 1;
    }

    if (backslashes % 2 === 1) {
      current += character;
      continue;
    }

    cells.push(current);
    current = '';
  }

  cells.push(current);

  return cells;
}

function splitTableCells(row: string): string[] {
  const trimmed = row.trim();

  if (!trimmed.includes('|')) {
    return [];
  }

  const inner = trimmed.startsWith('|') ? trimmed.slice(1) : trimmed;
  const withoutTrailing = inner.endsWith('|') ? inner.slice(0, -1) : inner;

  return splitUnescapedPipes(withoutTrailing).map((cell) => cell.trim());
}

function isDelimiterRow(row: string): boolean {
  const cells = splitTableCells(row);

  return (
    cells.length > 0 &&
    cells.every((cell) => cell.length > 0 && DELIMITER_CELL.test(cell))
  );
}

function isTableRow(row: string): boolean {
  const cells = splitTableCells(row);

  return cells.length > 0 && !isDelimiterRow(row);
}

function delimiterLine({
  prefix,
  columnCount,
  existingCells,
}: {
  prefix: string;
  columnCount: number;
  existingCells: string[];
}): string {
  const cells = Array.from({ length: columnCount }, (_, index) => {
    const cell = existingCells[index];

    return cell && DELIMITER_CELL.test(cell) ? cell : '---';
  });

  return `${prefix}| ${cells.join(' | ')} |`;
}

export function repairGfmTableDelimiters(source: string): string {
  const lines = source.split('\n');
  let fence: string | undefined;
  const repaired: string[] = [];

  for (let index = 0; index < lines.length; index += 1) {
    const line = lines[index];

    if (fence) {
      repaired.push(line);

      if (line.trim().startsWith(fence)) {
        fence = undefined;
      }

      continue;
    }

    const fenceMatch = line.trim().match(FENCE_OPEN);

    if (fenceMatch) {
      fence = fenceMatch[1][0].repeat(fenceMatch[1].length);
      repaired.push(line);
      continue;
    }

    const { prefix, body } = splitQuotePrefix(line);

    if (!isDelimiterRow(body)) {
      repaired.push(line);
      continue;
    }

    const previous = [...repaired]
      .reverse()
      .find((candidate) => candidate.trim().length > 0);

    if (!previous) {
      repaired.push(line);
      continue;
    }

    const previousSplit = splitQuotePrefix(previous);

    if (!isTableRow(previousSplit.body)) {
      repaired.push(line);
      continue;
    }

    const headerCells = splitTableCells(previousSplit.body);
    const delimiterCells = splitTableCells(body);

    if (headerCells.length === delimiterCells.length) {
      repaired.push(line);
      continue;
    }

    repaired.push(
      delimiterLine({
        prefix,
        columnCount: headerCells.length,
        existingCells: delimiterCells,
      }),
    );
  }

  return repaired.join('\n');
}
